<template>
  <div class="season">
    <card is-large has-outline>
      <form class="form">
        <div class="form__group">
          <text-input label="Discount Code" v-model="form.code" :required="true" />
          <span
            class="error"
            v-if="codeExists">Code already exists, please use a discount unique code</span>
        </div>
        <div class="form__group">
          <select-input
            label="Currency"
            v-model="form.currency" :options="currencyTypes" :required="true" />
        </div>
        <div class="form__group">
          <select-input
            label="Type"
            v-model="form.type" :options="discountTypes" :required="true" />
        </div>
        <div class="form__group">
          <number-input
            :label="valueLabel" v-model.number="form.value" :max="maximumVal" :required="true" />
          <span
            class="error"
            v-if="maximumVal && form.value > maximumVal"
            >Please enter a value less than 100%</span>
        </div>
        <div class="form__group">
          <date-input label="Expiry date" v-model="form.endDate" />
        </div>
        <div class="form__group">
          <number-input label="Limit" v-model.number="form.limit" />
        </div>
        <div class="form__submit">
          <button :disabled="disabled || !formIsValid" @click.prevent="submit">Save</button>
          <button
            v-if="code"
            @click.prevent="remove"
            class="is-light" :disabled="disabled">Delete</button>
        </div>
      </form>
    </card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';
import { DATEFORMAT } from '@/utilities/time';
import TextInput from '@/components/TextInput.vue';
import Card from '@/components/Card.vue';
import NumberInput from '@/components/NumberInput.vue';
import DateInput from '@/components/DateInput.vue';
import SelectInput from '@/components/SelectInput.vue';

export default {
  name: 'SeasonForm',
  components: {
    TextInput,
    NumberInput,
    Card,
    DateInput,
    SelectInput,
  },
  props: {
    id: String,
    code: String,
    type: String,
    currency: String,
    limit: Number,
    value: Number,
    endDate: Object,
    disabled: Boolean,
  },
  data() {
    return {
      form: {},
      discountTypes: [
        { key: 1, value: 'percentage', label: 'Percentage' },
        { key: 2, value: 'flat', label: 'Flat' },
      ],
      currencyTypes: [
        { key: 1, value: 'NGN', label: 'NGN' },
        { key: 2, value: 'GBP', label: 'GBP' },
      ],
    };
  },
  watch: {
    $route() {
      this.setForm();
    },
  },
  methods: {
    setForm() {
      const { id, endDate, ...props } = this.$props;
      const expiryDate = endDate && dayjs.unix(endDate.seconds).format(DATEFORMAT);
      const isFlat = props.type === 'flat';
      this.form = {
        code: props.code || '',
        type: props.type || 'flat',
        currency: props.currency || 'NGN',
        value: isFlat ? (props.value / 100) : props.value,
        endDate: expiryDate || null,
        limit: props.limit,
      };
    },
    submit() {
      const { limit, endDate, ...formValue } = this.form;
      const isFlat = this.form.type === 'flat';
      formValue.value = isFlat ? this.form.value * 100 : this.form.value;
      if (endDate) {
        formValue.endDate = dayjs(endDate).toDate();
      }
      if (limit) {
        formValue.limit = limit;
      }
      this.$emit('submit', formValue);
    },
    remove() {
      this.$emit('remove');
    },
  },
  created() {
    this.setForm();
  },
  computed: {
    ...mapGetters(['getAllDiscounts']),
    existingCodes() {
      return this.getAllDiscounts.map(({ code }) => code);
    },
    codeExists() {
      return (this.form.code !== this.code) && this.existingCodes.includes(this.form.code);
    },
    maximumVal() {
      return this.form.type === 'percentage' ? 100 : null;
    },
    valueLabel() {
      return this.form.type === 'percentage' ? 'Percentage Discount' : 'Amount';
    },
    formIsValid() {
      const {
        type,
        value,
        code,
        currency,
      } = this.form;
      const checkValue = type === 'percentage' ? (value <= 100) : value;
      return code && type && currency && checkValue && !this.codeExists;
    },
  },
};
</script>

<style lang="scss" scoped>
.season {
  max-width: 520px;
  margin: 30px auto;
  .form {
    text-align: left;
    &__group + .form__group {
      margin-top: 20px;
    }
    &__submit {
      margin-top: 30px;
      display: flex;
      justify-content: space-between;
    }
    .error {
      margin-top: 5px;
      font-size: var(--font-input);
      color: var(--color-link-inactive);
      font-weight: 400;
    }
  }
}
</style>
